import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: { label: string; onClick: () => void };
  duration?: number;
}

interface ToastContextValue {
  toast: {
    success: (message: string, options?: { action?: Toast['action']; duration?: number }) => void;
    error: (message: string, options?: { action?: Toast['action']; duration?: number }) => void;
    info: (message: string, options?: { action?: Toast['action']; duration?: number }) => void;
  };
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: ToastType, message: string, options?: { action?: Toast['action']; duration?: number }) => {
    const id = `${Date.now()}-${Math.random()}`;
    const duration = options?.duration ?? (options?.action ? 5000 : 3000);

    setToasts(prev => [...prev, { id, message, type, action: options?.action, duration }]);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (message: string, options?: { action?: Toast['action']; duration?: number }) => addToast('success', message, options),
    error: (message: string, options?: { action?: Toast['action']; duration?: number }) => addToast('error', message, options),
    info: (message: string, options?: { action?: Toast['action']; duration?: number }) => addToast('info', message, options),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 max-w-sm">
          {toasts.map(t => (
            <div
              key={t.id}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg backdrop-blur-sm animate-slide-in ${
                t.type === 'success' ? 'bg-teal-500/90 text-white' :
                t.type === 'error' ? 'bg-red-500/90 text-white' :
                'bg-[#1A1F26]/90 text-white ring-1 ring-white/[0.08]'
              }`}
            >
              {t.type === 'success' && <CheckCircle size={18} />}
              {t.type === 'error' && <AlertCircle size={18} />}
              {t.type === 'info' && <Info size={18} />}
              <span className="flex-1 text-sm font-medium">{t.message}</span>
              {t.action && (
                <button
                  onClick={() => { t.action?.onClick(); removeToast(t.id); }}
                  className="text-sm font-semibold underline hover:no-underline"
                >
                  {t.action.label}
                </button>
              )}
              <button onClick={() => removeToast(t.id)} className="p-1 hover:bg-white/20 rounded">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}
